import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import API, { UPLOADS_URL } from '../api/axios';

const SEO = ({ title, description, keywords, ogImage, canonical, schema, article, slug }) => {
  const siteName = 'St. Andrews World School';
  const defaultDescription = 'St. Andrews World School, Siddharth Vihar is a premier school committed to providing quality education with state-of-the-art facilities and dedicated faculty.';
  const siteUrl = process.env.REACT_APP_SITE_URL || 'https://sawssiddharthvihar.in';

  const [dbSeo, setDbSeo] = useState(null);

  useEffect(() => {
    if (!slug) {
      setDbSeo(null);
      return;
    }
    let cancelled = false;
    API.get(`/seo/${slug}`)
      .then(res => {
        if (!cancelled && res.data?.data) setDbSeo(res.data.data);
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [slug]);

  const metaTitle = (dbSeo?.meta_title || title) 
    ? `${dbSeo?.meta_title || title} | ${siteName}` 
    : `${siteName} | School of Excellence`;
  const metaDesc = dbSeo?.meta_description || description || defaultDescription;
  const metaKeywords = dbSeo?.meta_keywords || keywords;
  const metaCanonical = dbSeo?.canonical_url || canonical;
  const metaImage = dbSeo?.og_image
    ? (dbSeo.og_image.startsWith('http') ? dbSeo.og_image : `${UPLOADS_URL}/${dbSeo.og_image}`)
    : (ogImage || `${siteUrl}/og-image.jpg`);
  const ogType = article ? 'article' : 'website';
  const schemaArray = Array.isArray(schema) ? schema : (schema ? [schema] : []);

  return (
    <Helmet>
      <title>{metaTitle}</title>
      {dbSeo?.no_index ? (
        <meta name="robots" content="noindex, nofollow" />
      ) : (
        <meta name="robots" content="index, follow" />
      )}
      <meta name="description" content={metaDesc} />
      {metaKeywords && <meta name="keywords" content={metaKeywords} />}
      <link rel="canonical" href={metaCanonical || siteUrl} />
      <link rel="icon" type="image/png" sizes="32x32" href={`${siteUrl}/favicon.png`} />
      <link rel="shortcut icon" href={`${siteUrl}/favicon.png`} />
      <link rel="apple-touch-icon" href={`${siteUrl}/favicon.png`} />
      <meta property="og:title" content={metaTitle} />
      <meta property="og:description" content={metaDesc} />
      <meta property="og:image" content={metaImage} />
      <meta property="og:type" content={ogType} />
      <meta property="og:site_name" content={siteName} />
      <meta property="og:url" content={metaCanonical || siteUrl} />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={metaTitle} />
      <meta name="twitter:description" content={metaDesc} />
      <meta name="twitter:image" content={metaImage} />
      {article && (
        <>
          {article.publishedTime && <meta property="article:published_time" content={article.publishedTime} />}
          {article.modifiedTime && <meta property="article:modified_time" content={article.modifiedTime} />}
          {article.author && <meta property="article:author" content={article.author} />}
          {article.tags && article.tags.map((tag, idx) => (
            <meta key={idx} property="article:tag" content={tag} />
          ))}
        </>
      )}
      {schemaArray.map((obj, idx) => (
        <script key={idx} type="application/ld+json">
          {JSON.stringify(obj)}
        </script>
      ))}
    </Helmet>
  );
};

export default SEO;
